<template>
  <section class="flex justify-center flex-col gap-12">
    <div
      class="grid grid-cols-1 xl:grid-cols-2 gap-5 items-center container mx-auto px-4 py-20"
    >
      <div id="hero-title">
        <div class="w-24 h-24 opacity-10 -mb-10">
          <div class="grid grid-cols-4 gap-1.5">
            <div
              v-for="(_, index) in [...Array(16)]"
              :key="index"
              class="w-3 h-3 bg-gray-400 rounded-full"
            ></div>
          </div>
        </div>
        <h1
          class="mt-8 text-6xl lg:text-7xl font-[500] text-h-primary-900"
          v-motion
          :initial="initial.left"
          :enter="enter"
        >
          {{
            $t("labels.generic", {
              pt: "Porque aprender",
              en: "Because learning",
            })
          }}

          {{
            $t("labels.generic", {
              pt: "pode ser do seu jeito e no seu ritmo.",
              en: "can be your way and at your pace",
            })
          }}
        </h1>
      </div>
      <div
        id="hero-image"
        class="justify-self-end"
        v-motion
        :initial="initial.right"
        :enter="enter"
      >
        <HeroComponent />

        <div class="w-full xl:absolute flex justify-end mt-8">
          <div class="top-0 left-0 -mt-14 w-24 h-24 opacity-10">
            <div class="grid grid-cols-4 gap-1.5">
              <div
                v-for="(_, index) in [...Array(16)]"
                :key="index"
                class="w-3 h-3 bg-h-primary-300 rounded-full"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      id="how-works"
      class="w-full how-works mt-20 flex justify-center items-center flex-col"
    >
      <h3
        v-motion
        :initial="initial.left"
        :enter="{
          opacity: 1,
          x: 0,
          transition: {
            stiffness: 30,
            delay: 500,
          },
        }"
      >
        <span
          class="relative span-jost text-4xl flex flex-col items-center justify-center gap-1 mb-10"
        >
          {{
            $t("labels.generic", { pt: "Como funciona?", en: "How it works" })
          }}
          <div class="bg-h-secondary-500 h-1 w-6/12 rounded-full"></div>
        </span>
      </h3>
      <div
        class="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 items-center gap-5 px-5 lg:px-0"
      >
        <div v-for="(text, i) in texts" :key="text">
          <div
            class="bg-white shadow-md rounded-md p-10 relative group hover:-mt-2 hover:shadow-xl flex flex-col justify-center gap-5 items-center text-center !min-h-[330px] transition-all"
            v-motion
            :initial="initial.left"
            :enter="{
              opacity: 1,
              x: 0,
              transition: {
                stiffness: 30,
                delay: (i + 1) * 300,
              },
            }"
          >
            <div
              class="bg-h-primary-50 group-hover:bg-h-primary-100 rounded-2xl p-5 w-fit"
            >
              <v-icon
                :name="text.image"
                :scale="2"
                class="text-h-primary-500"
              />
            </div>

            <h4 class="text-h-primary-500">
              {{ $t("labels.generic", { pt: text.title, en: text.titleEN }) }}
            </h4>

            <p class="-mt-2 text-h-neutral-500">
              {{ $t("labels.generic", { pt: text.text, en: text.textEN }) }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div
      id="partners"
      class="mt-20 container mx-auto flex flex-col justify-center items-center px-16"
    >
      <h3
        v-motion
        :initial="initial.left"
        :visible="{
          opacity: 1,
          x: 0,
          transition: {
            stiffness: 30,
          },
        }"
      >
        <span
          class="relative span-jost text-4xl flex flex-col items-center justify-center gap-1"
        >
          {{ $t("labels.generic", { pt: "Parceiros", en: "Partners" }) }}
          <div class="bg-h-secondary-500 h-1 w-6/12 rounded-full"></div>
        </span>
      </h3>

      <div
        v-if="partners?.length !== 0"
        class="flex flex-wrap items-center justify-center w-full mt-16 mb-24 opacity-50 gap-12"
      >
        <img
          v-for="(img, index) in partners"
          class="invert filter w-40 h-fit mx-5"
          :key="index"
          :src="getFileUrl(img)"
          loading="lazy"
        />
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { useThemeStore } from "@/store/theme";
import { getFileUrl } from "@/utils/generics.util";
import { computed, defineComponent } from "vue";
import HeroComponent from "./hero-component.vue";

export default defineComponent({
  name: "landingView",
  components: { HeroComponent },
  data() {
    return {
      initial: {
        left: {
          x: -100,
          opacity: 0,
        },
        right: {
          x: 100,
          opacity: 0,
        },
      },
      enter: {
        opacity: 1,
        x: 0,
        transition: {
          stiffness: 30,
        },
      },
      texts: [
        {
          title: "Inteligência Artificial",
          titleEN: "Artificial Intelligence",
          text: "Por meio de uma Inteligência Artificial,  a plataforma avalia e considera o ritmo do aluno, seus conhecimentos prévios, os resultados de avaliações e a atenção dispensada no conteúdo já ministrado.",
          textEN:
            "Through an Artificial Intelligence, the platform evaluates and considers the pace of the student, their previous knowledge, the results of evaluations and the attention given to the content already ministered.",
          image: "fa-robot",
        },
        {
          title: "Chatbot",
          titleEN: "Chatbot",
          text: "Dentro da plataforma você terá ajuda do assistente virtual Homero, que fará com que a sua jornada de aprendizado seja facilitada ao atuar de forma ativa com apresentação do conteúdo, realização de perguntas e feedbacks. ",
          textEN:
            "Within the platform, you will have the help of Homero's virtual assistant, who will make your learning journey easier by actively presenting content, asking questions and providing feedback.",
          image: "md-markchatunread-twotone",
        },
        {
          title: "Conteúdo Personalizado",
          titleEN: "Custom content",
          text: "A plataforma tem a capacidade de detectar a melhor forma de entregar o conteúdo individualmente para cada aluno, assim tornando a experiência de ensino mais enriquecedora e única.",
          textEN:
            "The platform has the ability to detect the best way to deliver content individually to each student, thus making the teaching experience more enriching and unique.",
          image: "md-dashboardcustomize-twotone",
        },
      ] as any,
    };
  },
  setup() {
    const storeTheme = useThemeStore;

    const partners = computed(() => storeTheme.partners);

    return { partners, getFileUrl };
  },
});
</script>

<style lang="scss" src="./style.scss"></style>
